import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { renderAsync } from 'docx-preview';
import type { Attachment } from '../../types';

export interface AttachmentPreviewModalProps {
  open: boolean;
  attachment: Attachment | null;
  onClose: () => void;
}

type LoadState = 'loading' | 'ok' | 'missing';

function isDocxFileName(name: string): boolean {
  return /\.docx$/i.test(name.trim());
}

function isLegacyDocFileName(name: string): boolean {
  return /\.doc$/i.test(name.trim()) && !/\.docx$/i.test(name.trim());
}

/** .docx：docx-preview 按 Word 结构渲染，版式保留优于纯 HTML 转换 */
function DocxHtmlPreview(props: {
  path: string;
  onMissing: () => void;
}): React.ReactElement {
  const { path, onMissing } = props;
  const hostRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);
    host.innerHTML = '';

    (async () => {
      try {
        const res = await fetch(path, { cache: 'no-cache' });
        if (cancelled) return;
        if (!res.ok) {
          onMissing();
          return;
        }
        const blob = await res.blob();
        await renderAsync(blob, host, undefined, {
          className: 'docx-dash',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          renderAltChunks: true,
        });
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      host.innerHTML = '';
    };
  }, [path, onMissing]);

  if (error) {
    return (
      <div className="dash-attachment-preview__missing">
        文档无法解析，请尝试下载后使用 Word 打开
      </div>
    );
  }

  return (
    <div className="dash-attachment-preview__frame dash-attachment-preview__frame--docx">
      {loading ? (
        <div className="dash-attachment-preview__docx-loading">
          <Spin tip="正在渲染 Word 文档…" />
        </div>
      ) : null}
      <div ref={hostRef} className="dash-attachment-preview__docx-host" />
    </div>
  );
}

/** 旧版 .doc：浏览器内无法可靠解析，提供下载 */
function LegacyDocPreview(props: { path: string; label: string }): React.ReactElement {
  const { path, label } = props;
  const [phase, setPhase] = useState<'check' | 'ok' | 'missing'>('check');

  useEffect(() => {
    let cancelled = false;
    setPhase('check');
    fetch(path, { method: 'HEAD', cache: 'no-cache' })
      .then((res) => {
        if (cancelled) return;
        setPhase(res.ok ? 'ok' : 'missing');
      })
      .catch(() => {
        if (!cancelled) setPhase('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (phase === 'check') {
    return (
      <div className="dash-attachment-preview__loading">
        <Spin tip="正在检查文件…" />
      </div>
    );
  }

  if (phase === 'missing') {
    return <div className="dash-attachment-preview__missing">文件未找到</div>;
  }

  return (
    <div className="dash-attachment-preview__frame dash-attachment-preview__frame--legacy-doc">
      <p className="dash-attachment-preview__legacy-doc-hint">
        旧版 .doc 格式无法在浏览器内预览，请下载后使用 Word 或其他软件打开。
      </p>
      <a className="dash-attachment-preview__download-link" href={path} download={label}>
        下载 {label}
      </a>
    </div>
  );
}

const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;

function ImagePreviewWithZoom(props: {
  path: string;
  label: string;
  onMissing: () => void;
}): React.ReactElement {
  const { path, label, onMissing } = props;
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [zoomPercent, setZoomPercent] = useState(100);

  useEffect(() => {
    setNatural(null);
    setZoomPercent(100);
  }, [path]);

  const zoomOut = useCallback(() => {
    setZoomPercent((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  }, []);
  const zoomIn = useCallback(() => {
    setZoomPercent((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  }, []);
  const resetZoom = useCallback(() => {
    setZoomPercent(100);
  }, []);

  const onImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    setNatural({ w: el.naturalWidth, h: el.naturalHeight });
  }, []);

  const pixelW =
    natural != null ? Math.round((natural.w * zoomPercent) / 100) : undefined;

  return (
    <div className="dash-attachment-preview__image-wrap">
      <div className="dash-attachment-preview__image-toolbar">
        <button type="button" className="dash-attachment-preview__zoom-btn" onClick={zoomOut}>
          缩小
        </button>
        <span className="dash-attachment-preview__zoom-label">{zoomPercent}%</span>
        <button type="button" className="dash-attachment-preview__zoom-btn" onClick={zoomIn}>
          放大
        </button>
        <button type="button" className="dash-attachment-preview__zoom-btn" onClick={resetZoom}>
          重置
        </button>
      </div>
      <div className="dash-attachment-preview__frame dash-attachment-preview__frame--image">
        <div className="dash-attachment-preview__image-scroll">
          <img
            src={path}
            alt={label}
            onLoad={onImgLoad}
            onError={() => onMissing()}
            style={
              natural
                ? {
                    width: `${pixelW}px`,
                    height: 'auto',
                    maxWidth: zoomPercent <= 100 ? '100%' : undefined,
                    display: 'block',
                    margin: '0 auto',
                  }
                : { maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }
            }
          />
        </div>
      </div>
    </div>
  );
}

export function AttachmentPreviewModal(
  props: AttachmentPreviewModalProps,
): React.ReactElement {
  const { open, attachment, onClose } = props;
  const [loadState, setLoadState] = useState<LoadState>('loading');

  const onMissing = useCallback(() => {
    setLoadState('missing');
  }, []);

  useEffect(() => {
    if (!open || !attachment) {
      setLoadState('loading');
      return;
    }

    if (attachment.type === 'doc') {
      setLoadState('ok');
      return;
    }

    let cancelled = false;
    setLoadState('loading');

    const check = async (): Promise<void> => {
      try {
        const res = await fetch(attachment.path, {
          method: 'HEAD',
          cache: 'no-cache',
        });
        if (cancelled) return;
        if (res.ok) {
          setLoadState('ok');
          return;
        }
        const retry = await fetch(attachment.path, {
          method: 'GET',
          cache: 'no-cache',
          headers: { Range: 'bytes=0-0' },
        });
        if (cancelled) return;
        setLoadState(retry.ok ? 'ok' : 'missing');
      } catch {
        if (!cancelled) setLoadState('missing');
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [open, attachment]);

  const title = attachment?.label ?? '附件预览';

  const modalWidth =
    attachment?.type === 'doc' && attachment && isDocxFileName(attachment.label)
      ? 'min(960px, 96vw)'
      : 'min(920px, 94vw)';

  return (
    <Modal
      className="dash-attachment-preview-modal"
      title={title}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={modalWidth}
      centered
      destroyOnClose
    >
      {!attachment ? null : loadState === 'loading' ? (
        <div className="dash-attachment-preview__loading">
          <Spin tip="正在检查文件…" />
        </div>
      ) : loadState === 'missing' ? (
        <div className="dash-attachment-preview__missing">文件未找到</div>
      ) : attachment.type === 'image' ? (
        <ImagePreviewWithZoom
          path={attachment.path}
          label={attachment.label}
          onMissing={onMissing}
        />
      ) : attachment.type === 'pdf' ? (
        <div className="dash-attachment-preview__frame dash-attachment-preview__frame--pdf">
          <iframe
            title={attachment.label}
            src={attachment.path}
            onError={() => onMissing()}
          />
        </div>
      ) : isDocxFileName(attachment.label) ? (
        <DocxHtmlPreview path={attachment.path} onMissing={onMissing} />
      ) : isLegacyDocFileName(attachment.label) ? (
        <LegacyDocPreview path={attachment.path} label={attachment.label} />
      ) : (
        <LegacyDocPreview path={attachment.path} label={attachment.label} />
      )}
    </Modal>
  );
}
